import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, Download, Moon, Sun, Users } from 'lucide-react';
import useDarkMode from '../hooks/useDarkMode';
import useAuth from '../hooks/useAuth';
import UserMenu from './UserMenu';

interface LayoutProps {
  children: React.ReactNode;
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg text-sm font-exo font-medium transition-all duration-200 ${
        isActive
          ? "bg-darling-lilac/10 dark:bg-darling-lilac/20 text-darling-lilac border border-darling-lilac/20"
          : "text-soft-black/70 dark:text-light-gray/80 hover:bg-light-gray/50 dark:hover:bg-deep-purple/20 hover:text-soft-black dark:hover:text-white"
      }`}
    >
      {children}
    </Link>
  );
}

export default function Layout({ children }: LayoutProps) {
  const { dark, setDark } = useDarkMode();
  const { isAdmin } = useAuth();

  const handleExportAll = () => {
    // Esta função será movida para um contexto ou prop quando necessário
    console.log('Export all functionality');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray via-white to-light-gray-alt dark:from-soft-black dark:via-deep-purple/20 dark:to-soft-black font-exo">
      {/* HEADER */}
      <header className="relative bg-white dark:bg-soft-black border-b border-light-gray-alt dark:border-deep-purple/30 shadow-sm">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-darling-lilac via-transparent to-ocean-water" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-darling-lilac/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-ocean-water/20 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Bar */}
          <div className="flex items-center justify-between py-4 border-b border-light-gray-alt/50 dark:border-deep-purple/20">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                  <picture>
                    <source media="(prefers-color-scheme: dark)" srcSet="/logo-cro-brasil-branco.png" />
                    <img 
                      src="/logo-cro-brasil.png" 
                      alt="CRO Brasil" 
                      className="h-8 w-auto dark:hidden"
                    />
                  </picture>
                  <img 
                    src="/logo-cro-brasil-branco.png" 
                    alt="CRO Brasil" 
                    className="h-8 w-auto hidden dark:block"
                  />
                </Link>
                <div className="hidden sm:block">
                  <div className="text-sm font-exo font-semibold text-soft-black dark:text-white">
                    CRO Brasil
                  </div>
                  <div className="text-xs text-soft-black/60 dark:text-light-gray/70 font-exo">
                    Comunidade de Embaixadores
                  </div>
                </div>
                
                {/* Navigation */}
                <nav className="hidden md:flex items-center gap-1 ml-8">
                  <NavLink to="/">Agenda</NavLink>
                  <NavLink to="/beneficios">Benefícios</NavLink>
                  {isAdmin && (
                    <NavLink to="/admin">
                      Gerenciar Convites
                    </NavLink>
                  )}
                </nav>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setDark(!dark)} 
                className="inline-flex items-center gap-2 rounded-lg border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray/50 dark:bg-deep-purple/20 hover:bg-light-gray dark:hover:bg-deep-purple/40 px-3 py-2 text-sm font-exo font-medium text-soft-black dark:text-white transition-all duration-200"
              >
                {dark ? <Sun className="h-4 w-4"/> : <Moon className="h-4 w-4"/>}
                <span className="hidden sm:inline">{dark ? 'Light' : 'Dark'}</span>
              </button>
              <button 
                onClick={handleExportAll} 
                className="inline-flex items-center gap-2 rounded-lg border border-darling-lilac/30 bg-darling-lilac/10 hover:bg-darling-lilac/20 dark:bg-darling-lilac/20 dark:hover:bg-darling-lilac/30 px-3 py-2 text-sm font-exo font-medium text-darling-lilac transition-all duration-200"
              >
                <Download className="h-4 w-4"/> 
                <span className="hidden sm:inline">Exportar .ics</span>
                <span className="sm:hidden">.ics</span>
              </button>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      {children}

      {/* FOOTER */}
      <footer className="mt-24 border-t border-light-gray-alt dark:border-deep-purple/50 bg-gradient-to-r from-light-gray/80 via-white/60 to-light-gray/80 dark:from-soft-black/90 dark:via-deep-purple/30 dark:to-soft-black/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-sm text-soft-black/70 dark:text-light-gray/90 font-exo flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <div className="font-exo font-medium text-darling-lilac">
              © {new Date().getFullYear()} CRO Brasil — Agenda de Embaixadores
            </div>
            <div className="mt-1 text-xs text-soft-black/50 dark:text-light-gray/80 font-exo font-light">
              Juntos somos mais fortes • Ousar e aprender • Excelência em cada detalhe
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium bg-white/80 dark:bg-soft-black/90 backdrop-blur border-light-gray-alt dark:border-deep-purple/50 bg-darling-lilac/10 dark:bg-darling-lilac/20 text-darling-lilac dark:text-darling-lilac/90 border-darling-lilac/20 dark:border-darling-lilac/30 font-exo">CRO Brasil</span>
          </div>
        </div>
      </footer>
    </div>
  );
}